/**
 * mwApp.reports module <br>
 * Work with reports: show/add/edit/remove
 *
 * @namespace mwApp/reports
 */

/**
 * ConfigRoutes
 * Configurates module routes
 *
 * @class ConfigRoutes
 * @memberof mwApp/reports
 * @see mwApp/reports
 *
 * @ngdoc config
 * @name mwApp/reports.config:ConfigRoutes
 */
/* @ngInject */

export default function ConfigRoutes ($stateProvider) {
  $stateProvider

  /**
   * abstract state for reports
   */
    .state('app.reports', {
      abstract: true,
      url: '',
      template: '<ui-view/>'
    })

  /**
   * abstract state for /reports
   */
    .state('app.reports.reports', {
      abstract: true,
      url: '/reports',
      template: '<ui-view/>'
    })

  /**
   * controller {@link mwApp/reports.controller:ReportsCtrl}
   * @name /reports
   * @memberof mwApp/reports.config:ConfigRoutes
   */
    .state('app.reports.reports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/reports.html',
          controller: 'ReportsCtrl as vm'
        },
        'new@app.reports.reports.list': {
          templateUrl: 'components/reports/views/addReport.html',
          controller: 'AddReportCtrl as vm'
        },
        'templates@app.reports.reports.list': {
          templateUrl: 'components/reports/views/reportTemplates.html',
          controller: 'ReportTemplatesCtrl as vm'
        },
        'reports@app.reports.reports.list': {
          templateUrl: 'components/reports/views/generatedReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Reports',
      access: 'viewSubscriberReport',
      resolve: {
        scope: ['$state', 'authSvc', function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_REPORTS_ENABLED')) {
            return $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            reportType: ''
          }
        }]
      }
    })

  /**
   * abstract state for /streamreports
   */
    .state('app.reports.streamreports', {
      abstract: true,
      url: '/streamreports',
      template: '<ui-view/>'
    })

  /**
   * controller {@link mwApp/reports.controller:ReportsCtrl}
   * @name /reports
   * @memberof mwApp/reports.config:ConfigRoutes
   */
    .state('app.reports.streamreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/streamReports.html',
          controller: 'ReportsCtrl as vm'
        },
        'stream@app.reports.streamreports.list': {
          templateUrl: 'components/reports/views/addStreamReport.html',
          controller: 'AddStreamReportCtrl as vm'
        },
        'templates@app.reports.streamreports.list': {
          templateUrl: 'components/reports/views/reportStreamTemplates.html',
          controller: 'ReportTemplatesCtrl as vm'
        },
        'reports@app.reports.streamreports.list': {
          templateUrl: 'components/reports/views/generatedStreamReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Stream Reports',
      access: 'viewStreamReport',
      resolve: {
        scope: ['$state', 'authSvc', function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_STREAM_REPORTS_ENABLED')) {
            return $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            reportType: 'stream'
          }
        }]
      }
    })

  /**
   * abstract state for /subscribersreports
   */
    .state('app.reports.subscribersreports', {
      abstract: true,
      url: '/subscribersreports',
      template: '<ui-view/>'
    })

  /**
   * controller {@link mwApp/reports.controller:ReportsCtrl}
   * @name /reports
   * @memberof mwApp/reports.config:ConfigRoutes
   */
    .state('app.reports.subscribersreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/subscribersReports.html',
          controller: 'SubscribersReportsCtrl as vm'
        },
        'subscribers@app.reports.subscribersreports.list': {
          templateUrl: 'components/reports/views/addSubscribersReport.html',
          controller: 'AddSubscribersReportCtrl as vm'
        },
        'reports@app.reports.subscribersreports.list': {
          templateUrl: 'components/reports/views/generatedSubscribersReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'CPS Report',
      access: 'viewCpsReport',
      resolve: {
        scope: ['$state', 'authSvc', function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_CPS_REPORTS_ENABLED')) {
            return $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            reportType: 'subscribers'
          }
        }]
      }
    })

  /**
   * abstract state for /subscribersactivityreports
   */
    .state('app.reports.subscribersactivityreports', {
      abstract: true,
      url: '/subscribersactivityreports',
      template: '<ui-view/>'
    })

  /**
   * controller {@link mwApp/reports.controller:SubscribersActivityReportsCtrl}
   * @name /reports
   * @memberof mwApp/reports.config:ConfigRoutes
   */
    .state('app.reports.subscribersactivityreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/subscribersActivityReports.html',
          controller: 'SubscribersActivityReportsCtrl as vm'
        },
        'subscribers_activity@app.reports.subscribersactivityreports.list': {
          templateUrl: 'components/reports/views/addSubscribersActivityReports.html',
          controller: 'AddSubscribersActivityReportsCtrl as vm'
        },
        'reports@app.reports.subscribersactivityreports.list': {
          templateUrl: 'components/reports/views/generatedSubscribersActivityReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Subscribers Activity Reports',
      access: 'viewSubscriberActivityReport',
      resolve: {
        scope: ['$state', 'authSvc', function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_SUBSCRIBER_ACTIVITY_REPORTS_ENABLED')) {
            return $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            reportType: 'subscribers_activity'
          }
        }]
      }
    })

  /**
   * abstract state for /usersreports
   */
    .state('app.reports.usersreports', {
      abstract: true,
      url: '/usersreports',
      template: '<ui-view/>'
    })

  /**
   * controller {@link mwApp/reports.controller:ReportsCtrl}
   * @name /reports
   * @memberof mwApp/reports.config:ConfigRoutes
   */
    .state('app.reports.usersreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/usersReports.html',
          controller: 'UsersReportsCtrl as vm'
        },
        'users@app.reports.usersreports.list': {
          templateUrl: 'components/reports/views/addUsersReports.html',
          controller: 'AddUsersReportsCtrl as vm'
        },
        'templates@app.reports.usersreports.list': {
          templateUrl: 'components/reports/views/reportUsersTemplates.html',
          controller: 'ReportTemplatesCtrl as vm'
        },
        'reports@app.reports.usersreports.list': {
          templateUrl: 'components/reports/views/generatedUsersReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Activation Audit Reports',
      access: 'viewActivationAuditReport',
      resolve: {
        scope: ['$state', 'authSvc', function ($state, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_ACTIVATION_AUDIT_REPORTS_ENABLED')) {
            return $state.go('app.404')
          }
          // use object 'scope' to share object between different views
          return {
            reportType: 'users'
          }
        }]
      }
    })

    /**
     * abstract state for /financialreports
     */
    .state('app.reports.financialreports', {
      abstract: true,
      url: '/financialreports',
      template: '<ui-view/>'
    })

    /**
     * controller {@link mwApp/reports.controller:ReportsCtrl}
     * @name /reports
     * @memberof mwApp/reports.config:ConfigRoutes
     */
    .state('app.reports.financialreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/financialReports.html',
          controller: 'FinancialReportsCtrl as vm'
        },
        'users@app.reports.financialreports.list': {
          templateUrl: 'components/reports/views/addFinancialReport.html',
          controller: 'AddFinancialReportCtrl as vm'
        },
        'templates@app.reports.financialreports.list': {
          templateUrl: 'components/reports/views/reportFinancialTemplates.html',
          controller: 'ReportTemplatesCtrl as vm'
        },
        'reports@app.reports.financialreports.list': {
          templateUrl: 'components/reports/views/generatedFinancialReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Financial Reports',
      access: '',
      resolve: {
        subscriptions: /* @ngInject */ function ($state, ReportFinancialSeed, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_FINANCIAL_REPORTS_ENABLED')) {
            $state.go('app.404')
          }
          return ReportFinancialSeed.loadExtra({})
            .then(data => data.subscriptions)
            .catch(() => ({})) // suppress error in case of failed loading
        },
        scope: /* @ngInject */function (subscriptions) {
          return { reportType: 'financial', subscriptions }
        },
        extra: /* @ngInject */ function (ReportFinancial) {
          return ReportFinancial.loadExtra()
        }
      }

    })

    /**
     * abstract state for /transactionreports
     */
    .state('app.reports.transactionreports', {
      abstract: true,
      url: '/transactionreports',
      template: '<ui-view/>'
    })

    /**
     * Transaction Report
     */
    .state('app.reports.transactionreports.list', {
      url: '',
      views: {
        '': {
          templateUrl: 'components/reports/views/transactionReports.html',
          controller: 'TransactionReportsCtrl as vm'
        },
        'users@app.reports.transactionreports.list': {
          templateUrl: 'components/reports/views/addTransactionReport.html',
          controller: 'AddTransactionReportCtrl as vm'
        },
        'templates@app.reports.transactionreports.list': {
          templateUrl: 'components/reports/views/reportTransactionTemplates.html',
          controller: 'ReportTemplatesCtrl as vm'
        },
        'reports@app.reports.transactionreports.list': {
          templateUrl: 'components/reports/views/generatedTransactionReports.html',
          controller: 'GeneratedReportsCtrl as vm'
        }
      },
      label: 'Transactions Reports',
      access: '',
      resolve: {
        subscriptions: /* @ngInject */ function ($state, ReportTransactionSeed, authSvc) {
          if (!authSvc.hasEnabledFlag('FEATURE_TRANSACTIONAL_REPORTS_ENABLED')) {
            $state.go('app.404')
          }
          return ReportTransactionSeed.loadExtra({})
            .then(data => data.subscriptions)
            .catch(() => ({})) // suppress error in case of failed loading
        },
        scope: /* @ngInject */function (subscriptions) {
          return { reportType: 'transaction', subscriptions }
        },
        extra: /* @ngInject */ function (ReportFinancial) {
          return ReportFinancial.loadExtra()
        }
      }
    })
}
